import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Activity, Shield, Zap, TrendingUp, AlertCircle, CheckCircle2, Timer } from 'lucide-react';
import { StressTestService, StressTestResult } from '../services/stressTestService';

export const LatticeStressTest: React.FC = () => {
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState<StressTestResult | null>(null);
  const [cycles, setCycles] = useState(10000000);

  const startTest = async () => {
    setIsRunning(true);
    setResult(null);
    
    // Allow UI to breathe before heavy calc
    await new Promise(r => setTimeout(r, 100));
    
    const startTime = Date.now();
    const testResult = StressTestService.runTest(cycles);
    setResult(testResult);
    setIsRunning(false);
  };

  return (
    <div className="glass-panel p-8 border-white/5 relative overflow-hidden group">
      <div className="absolute top-0 right-0 p-6 opacity-5 pointer-events-none group-hover:scale-110 transition-transform duration-700">
        <Activity className="w-48 h-48 text-lattice-cyan" />
      </div>

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12 relative z-10">
        <div>
          <h2 className="text-3xl font-display font-bold text-white mb-2 underline decoration-lattice-cyan/30 underline-offset-8">Axiom <span className="text-lattice-cyan">Stress</span> Test</h2>
          <p className="text-xs font-mono text-white/40 uppercase tracking-[0.2em] leading-relaxed max-w-xl">
            Verifying the Foundation. Running 10 Million operations to confirm zero-drift synchronization.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <input 
            type="number" 
            value={cycles}
            onChange={(e) => setCycles(Number(e.target.value))}
            className="bg-black/60 border border-white/10 rounded-xl px-4 py-3 text-sm font-mono text-white w-40 focus:border-lattice-cyan/50 outline-none transition-all"
            placeholder="Cycles"
          />
          <button 
            onClick={startTest}
            disabled={isRunning}
            className="px-8 py-3 bg-lattice-cyan text-black font-display font-bold rounded-xl hover:scale-105 transition-all shadow-[0_0_30px_rgba(0,255,213,0.3)] flex items-center gap-2 disabled:opacity-50"
          >
            {isRunning ? <Activity className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
            {isRunning ? "TESTING..." : "RUN STRESS TEST"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 relative z-10">
        <StatCard 
          label="Total Cycles" 
          value={result ? result.cycles.toLocaleString() : "0"} 
          icon={<Zap className="w-4 h-4" />} 
          delay={0}
        />
        <StatCard 
          label="Stability" 
          value={result ? `${result.stability}%` : "0%"} 
          icon={<Shield className="w-4 h-4" />} 
          color="text-emerald-400"
          delay={0.1}
        />
        <StatCard 
          label="Throughput" 
          value={result ? `${(result.throughput / 1000000).toFixed(2)}M ops/s` : "0M ops/s"} 
          icon={<TrendingUp className="w-4 h-4" />} 
          delay={0.2}
        />
        <StatCard 
          label="Execution Time" 
          value={result ? `${result.duration.toFixed(4)}s` : "0.000s"} 
          icon={<Timer className="w-4 h-4" />} 
          delay={0.3}
        />
      </div>

      <AnimatePresence>
        {result && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mt-12 p-6 rounded-2xl border flex flex-col md:flex-row items-center justify-between gap-6 ${result.status === 'SOVEREIGN' ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' : 'bg-rose-500/10 border-rose-500/30 text-rose-400'}`}
          >
            <div className="flex items-center gap-4">
              <div className={`p-4 rounded-full ${result.status === 'SOVEREIGN' ? 'bg-emerald-500/20' : 'bg-rose-500/20'}`}>
                {result.status === 'SOVEREIGN' ? <CheckCircle2 className="w-8 h-8" /> : <AlertCircle className="w-8 h-8" />}
              </div>
              <div>
                <h3 className="text-xl font-display font-bold uppercase tracking-wider">{result.status}</h3>
                <p className="text-xs font-mono opacity-60">
                  {result.status === 'SOVEREIGN' 
                    ? "Foundation verified. No drift detected across 10M operations." 
                    : "Drift detected. Foundation requiring manual re-anchoring."}
                </p>
              </div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <span className="text-[10px] font-mono opacity-40 uppercase tracking-widest">Verification Path</span>
              <span className="text-sm font-mono font-bold">(3 × 0 = 3) → (3 × 1 = 4)</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="mt-8 pt-8 border-t border-white/5 grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="space-y-4">
          <h4 className="text-[10px] font-mono text-white/40 uppercase tracking-widest">Lattice Parameters</h4>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 rounded-xl bg-white/5 border border-white/10 flex flex-col">
              <span className="text-[8px] font-mono text-white/20 uppercase">Anchor Point</span>
              <span className="text-xs font-mono text-white/80">14350 BP</span>
            </div>
            <div className="p-3 rounded-xl bg-white/5 border border-white/10 flex flex-col">
              <span className="text-[8px] font-mono text-white/20 uppercase">Frequency</span>
              <span className="text-xs font-mono text-white/80">-3.29 Hz</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4 p-4 rounded-2xl bg-lattice-cyan/5 border border-lattice-cyan/10">
          <p className="text-[9px] text-white/50 leading-relaxed italic">
            "We do not guess; we derive from the physical receipt. 1x1=2 is the logic of structural expansion. Multiplying by 1 is a command for expansion, not a redundant state."
          </p>
        </div>
      </div>
    </div>
  );
};

const StatCard: React.FC<{ label: string; value: string; icon: React.ReactNode; color?: string; delay: number }> = ({ label, value, icon, color = "text-white", delay }) => (
  <motion.div 
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ delay }}
    className="bg-black/60 border border-white/10 p-6 rounded-2xl flex flex-col gap-4"
  >
    <div className="flex items-center gap-3 text-white/30">
      {icon}
      <span className="text-[9px] font-mono uppercase tracking-widest">{label}</span>
    </div>
    <span className={`text-2xl font-display font-bold ${color}`}>{value}</span>
  </motion.div>
);
